import styles from './recipient-dropdown.module.css'

import { MdAdd } from '@react-icons/all-files/md/MdAdd'
import cn from 'clsx'
import React, { useEffect, useRef } from 'react'

import type { SavedGroup, UserSearchResult } from '@disco/api/server'
import { validateEmail } from '@disco/utils/validation'

import { Badge, Box, Text } from 'components/ds'

/**
 * Type for brief recipients (from UserSearchResult with type 'brief')
 */
type BriefRecipient = Extract<
  UserSearchResult,
  { type: 'brief' }
>['recipients'][number]

/**
 * Search option interface from the DISCO API
 */
export interface SearchOption {
  /** Unique identifier for the search result */
  id: string
  /** Value of the search result */
  value: string
  /** Display label for the search result */
  label: string
  /** Name of the user/business */
  name: string
  /** Email address of the user/business */
  email: string
  /** Type of the result ('user', 'business', 'group', 'email') - saved recipients are displayed as 'user' */
  type: string
  /** Whether this option has an error */
  error?: boolean
  /** Whether this option is disabled */
  disabled?: boolean
  /** Recipients of the saved group or brief */
  recipients?: SavedGroup['recipients'] | BriefRecipient[]
}

/**
 * Props for the RecipientDropdown component
 */
interface RecipientDropdownProps {
  /** Current input value for filtering */
  inputValue: string
  /** Array of search suggestions from the API */
  suggestions: SearchOption[]
  /** Whether search is in progress */
  loading: boolean
  /** Callback when a suggestion is selected */
  onSelect: (option: SearchOption) => void
  /** Callback when creating a new email recipient */
  onCreateNew: (email: string) => void
  /** Whether the dropdown should be visible */
  showDropdown: boolean
  /** Currently selected option index */
  selectedIndex: number
  /** Callback when mouse enters an option */
  onMouseEnterOption: (index: number) => void
  /** API error message to display */
  apiError?: string | null
  /** Allows creation of new recipients */
  allowCreateRecipient?: boolean
}

/**
 * RecipientDropdown - Autocomplete dropdown for email recipient selection
 *
 * This component displays search suggestions from the DISCO API in a dropdown format.
 * It supports different types of results (users, businesses, groups) and provides
 * visual indicators for each type using badges. The component also allows creating
 * new email recipients if they don't exist in the database.
 *
 * Features:
 * - Visual badges on the right side indicating recipient type (Contact, Mailing List, Email, Brief Recipients)
 * - Loading state with "Searching..." indicator
 * - Keyboard navigation with visual selection highlighting
 * - Mouse hover support that updates keyboard selection
 * - Option to add new email addresses not found in search
 * - Error state handling for invalid options
 * - Disabled state support for unavailable options
 * - Proper accessibility with ARIA roles and attributes
 *
 * Result types and their badge labels:
 * - 'user': Contact badge - Individual DISCO users with name and email
 * - 'business': Contact badge - Business entities with business name
 * - 'saved': Mailing List badge - Saved recipient groups with multiple recipients
 * - 'brief': Brief Recipients badge - Brief objects with their recipients
 * - 'email': Email badge - Direct email addresses
 *
 * @param props - Component props
 * @returns RecipientDropdown component
 *
 * @example
 * ```tsx
 * <RecipientDropdown
 *   inputValue="john"
 *   suggestions={searchResults}
 *   loading={false}
 *   onSelect={(option) => handleSelect(option)}
 *   onCreateNew={(email) => handleCreateNew(email)}
 *   showDropdown={true}
 *   selectedIndex={0}
 *   onMouseEnterOption={setSelectedIndex}
 *   apiError={null}
 * />
 * ```
 */
export const RecipientDropdown: React.FC<RecipientDropdownProps> = ({
  inputValue,
  suggestions,
  loading,
  onSelect,
  onCreateNew,
  showDropdown,
  selectedIndex,
  onMouseEnterOption,
  apiError,
  allowCreateRecipient = false,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Auto-scroll selected option into view when selection changes
  useEffect(() => {
    if (showDropdown && selectedIndex >= 0 && dropdownRef.current) {
      const dropdown = dropdownRef.current
      const selectedOption = dropdown.querySelector(
        `[data-option-index="${selectedIndex}"]`,
      ) as HTMLElement

      if (selectedOption && selectedOption.scrollIntoView) {
        selectedOption.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        })
      }
    }
  }, [selectedIndex, showDropdown])

  const getOptionBadgeLabel = (type: string) => {
    switch (type) {
      case 'user':
        return 'Contact'
      case 'business':
        return 'Business'
      case 'email':
        return 'Email'
      case 'saved':
        return 'Mailing List'
      case 'brief':
        return 'Brief Recipients'
      default:
        return 'Email'
    }
  }

  const getOptionDisplay = (option: SearchOption) => {
    switch (option.type) {
      case 'user':
        return option.email
      case 'business':
        return option.name
      case 'email':
        return option.email
      case 'saved':
        return option.name
      case 'brief':
        return option.name
      default:
        return option.name || option.email
    }
  }

  const getOptionSubtitle = (option: SearchOption) => {
    switch (option.type) {
      case 'user':
        return option.name
      default:
        return null
    }
  }

  const trimmed = inputValue.trim()
  const canCreate = allowCreateRecipient ? !!trimmed : validateEmail(trimmed)
  const shouldShowCreate =
    canCreate &&
    !suggestions.some(s => s.email?.toLowerCase() === trimmed.toLowerCase())

  if (
    !showDropdown ||
    (!loading &&
      !apiError &&
      suggestions.length === 0 &&
      !shouldShowCreate &&
      inputValue.length < 3)
  ) {
    return null
  }

  const createIndex = allowCreateRecipient ? 0 : suggestions.length
  const suggestionIndexOffset = allowCreateRecipient && shouldShowCreate ? 1 : 0

  const createLabel = allowCreateRecipient
    ? `Add new contact "${trimmed}"`
    : `Add "${trimmed}"`

  const createOption = shouldShowCreate ? (
    <Box
      key="create-option"
      data-option-index={createIndex}
      p="2"
      className={cn(
        styles.option,
        selectedIndex === createIndex && styles.optionSelected,
        suggestions.length > 0 && !allowCreateRecipient && styles.addOption,
      )}
      onMouseDown={e => {
        e.preventDefault()
      }}
      onClick={() => onCreateNew(trimmed)}
      onMouseEnter={() => onMouseEnterOption(createIndex)}
    >
      <div className={styles.optionContent}>
        <MdAdd size={16} />
        <Text size="2" weight="medium">
          {createLabel}
        </Text>
      </div>
    </Box>
  ) : null

  return (
    <Box
      id="recipient-dropdown"
      ref={dropdownRef}
      role="listbox"
      className={styles.dropdown}
    >
      {loading ? (
        <Box p="2">
          <Text size="2" color="gray">
            Searching...
          </Text>
        </Box>
      ) : (
        <>
          {allowCreateRecipient && createOption}
          {apiError && (
            <Box p="2">
              <Text size="2" color="red">
                {apiError}
              </Text>
            </Box>
          )}

          {suggestions.map((option, index) => {
            const optionIndex = index + suggestionIndexOffset
            return (
              <Box
                key={option.id}
                data-option-index={optionIndex}
                p="2"
                className={cn(
                  styles.option,
                  selectedIndex === optionIndex && styles.optionSelected,
                  option.disabled && styles.optionDisabled,
                )}
                onMouseDown={e => {
                  e.preventDefault()
                }}
                onClick={() => !option.disabled && onSelect(option)}
                onMouseEnter={() => onMouseEnterOption(optionIndex)}
              >
                {option.error ? (
                  <Text size="2" color="red">
                    {option.value}
                  </Text>
                ) : (
                  <div className={styles.optionContent}>
                    <div className={styles.optionText}>
                      <Text
                        size="2"
                        weight="regular"
                        className={styles.optionTitle}
                      >
                        {getOptionDisplay(option)}
                      </Text>
                      {getOptionSubtitle(option) && (
                        <Text
                          size="2"
                          weight="regular"
                          color="gray"
                          className={styles.optionSubtitle}
                        >
                          {getOptionSubtitle(option)}
                        </Text>
                      )}
                    </div>
                    <Badge size="1" variant="soft" color="gray">
                      {getOptionBadgeLabel(option.type)}
                    </Badge>
                  </div>
                )}
              </Box>
            )
          })}

          {!allowCreateRecipient && createOption}
        </>
      )}
    </Box>
  )
}
